import React, { useState, useEffect, useCallback, useRef, Component } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { GoogleMap, useJsApiLoader, Polygon, Marker } from '@react-google-maps/api';
import { TerraDraw, TerraDrawPolygonMode, TerraDrawSelectMode } from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import {
  MapPin,
  Plus,
  Pencil,
  Trash2,
  Save,
  Loader2,
  Eye,
  EyeOff,
  Search,
  Target,
  Layers,
  AlertCircle,
  Check,
  X
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const libraries = ['places'];

const mapContainerStyle = {
  width: '100%',
  height: '500px',
  borderRadius: '8px'
};

const defaultCenter = {
  lat: 4.7110,
  lng: -74.0721
};

const ZONE_COLORS = [
  '#E63946',
  '#457B9D',
  '#2A9D8F',
  '#E9C46A',
  '#F4A261',
  '#9B5DE5',
  '#00BBF9',
  '#00F5D4'
];

// Error Boundary for Google Maps errors
class MapErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Map Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-64 p-6 bg-muted/50 rounded-lg border border-dashed">
          <AlertCircle className="h-12 w-12 text-amber-500 mb-4" />
          <p className="text-center text-muted-foreground">
            Error al cargar el mapa. Por favor configura una API Key válida de Google Maps en Settings &gt; Maps.
          </p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Reintentar
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

export const CoverageMapSection = ({ mapsApiKey }) => {
  const { t } = useTranslation(['settings', 'common']);
  const [zones, setZones] = useState([]);
  const [config, setConfig] = useState({
    default_center: defaultCenter,
    default_zoom: 11,
    reject_outside_coverage: true,
    allow_manual_override: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  const [editingZone, setEditingZone] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [newPolygon, setNewPolygon] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, zone: null });
  const [testAddress, setTestAddress] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testMarker, setTestMarker] = useState(null);
  const [visibleZones, setVisibleZones] = useState({});
  
  const mapRef = useRef(null);
  const terraDrawRef = useRef(null);
  const [mapsReady, setMapsReady] = useState(false);
  const [mapsError, setMapsError] = useState(false);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: mapsApiKey || '',
    libraries,
  });

  // Check if Google Maps is properly loaded
  useEffect(() => {
    if (loadError) {
      setMapsError(true);
      setMapsReady(false);
      return;
    }
    
    if (isLoaded && window.google?.maps) {
      setMapsReady(true);
      setMapsError(false);
    } else {
      setMapsReady(false);
    }
  }, [isLoaded, loadError]);

  useEffect(() => {
    fetchZones();
    fetchConfig();
  }, []);

  useEffect(() => {
    // Initialize all zones as visible
    const visible = {};
    zones.forEach(z => { visible[z.id] = true; });
    setVisibleZones(visible);
  }, [zones]);

  // Cleanup TerraDraw on unmount
  useEffect(() => {
    return () => {
      if (terraDrawRef.current) {
        try {
          terraDrawRef.current.stop();
        } catch (e) {
          console.warn('Error stopping TerraDraw:', e);
        }
      }
    };
  }, []);

  const fetchZones = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/coverage/zones`);
      setZones(response.data.data || []);
    } catch (error) {
      console.error('Error fetching coverage zones:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/coverage/config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching coverage config:', error);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/coverage/config`, config);
      toast.success('Configuración de cobertura guardada');
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateZone = async () => {
    if (!newPolygon || newPolygon.length < 3 || !editingZone?.name) {
      toast.error('Por favor dibuja un polígono y proporciona un nombre');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/coverage/zones`, {
        name: editingZone.name,
        description: editingZone.description,
        polygon: newPolygon,
        color: editingZone.color || ZONE_COLORS[zones.length % ZONE_COLORS.length],
        is_active: true
      });
      
      setZones(prev => [...prev, response.data]);
      toast.success('Zona de cobertura creada');
      resetDrawing();
      setSheetOpen(false);
    } catch (error) {
      toast.error('Error al crear zona de cobertura');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateZone = async () => {
    if (!editingZone?.id) return;

    setSaving(true);
    try {
      const updateData = {
        name: editingZone.name,
        description: editingZone.description,
        color: editingZone.color,
        is_active: editingZone.is_active
      };
      
      if (newPolygon && newPolygon.length >= 3) {
        updateData.polygon = newPolygon;
      }

      const response = await axios.put(`${API_URL}/api/coverage/zones/${editingZone.id}`, updateData);
      
      setZones(prev => prev.map(z => z.id === editingZone.id ? response.data : z));
      toast.success('Zona de cobertura actualizada');
      resetDrawing();
      setSheetOpen(false);
    } catch (error) {
      toast.error('Error al actualizar zona');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteZone = async () => {
    if (!deleteDialog.zone) return;

    try {
      await axios.delete(`${API_URL}/api/coverage/zones/${deleteDialog.zone.id}`);
      setZones(prev => prev.filter(z => z.id !== deleteDialog.zone.id));
      toast.success('Zona de cobertura eliminada');
      setDeleteDialog({ open: false, zone: null });
    } catch (error) {
      toast.error('Error al eliminar zona');
    }
  };

  const handleToggleZone = async (zone) => {
    try {
      const response = await axios.post(`${API_URL}/api/coverage/zones/${zone.id}/toggle`);
      setZones(prev => prev.map(z => 
        z.id === zone.id ? { ...z, is_active: response.data.is_active } : z
      ));
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleTestAddress = async () => {
    if (!testAddress.trim()) return;

    try {
      const response = await axios.post(`${API_URL}/api/coverage/validate`, {
        address: testAddress
      });
      
      setTestResult(response.data);
      
      if (response.data.coordinates) {
        setTestMarker({
          lat: response.data.coordinates.lat,
          lng: response.data.coordinates.lng
        });
        
        // Center map on result
        if (mapRef.current) {
          mapRef.current.panTo(response.data.coordinates);
        }
      }
    } catch (error) {
      toast.error('Error al validar dirección');
    }
  };

  const initTerraDraw = useCallback((map) => {
    if (!map || terraDrawRef.current) return;

    try {
      const draw = new TerraDraw({
        adapter: new TerraDrawGoogleMapsAdapter({ map, lib: window.google.maps }),
        modes: [
          new TerraDrawPolygonMode({
            allowSelfIntersection: false,
            styles: {
              fillColor: editingZone?.color || ZONE_COLORS[0],
              fillOpacity: 0.3,
              outlineColor: editingZone?.color || ZONE_COLORS[0],
              outlineWidth: 2,
            }
          }),
          new TerraDrawSelectMode({
            flags: {
              polygon: {
                feature: {
                  draggable: true,
                  coordinates: {
                    midpoints: true,
                    draggable: true,
                    deletable: true
                  }
                }
              }
            }
          })
        ]
      });

      // Listen for when a polygon is created
      draw.on('finish', (id) => {
        const snapshot = draw.getSnapshot();
        const feature = snapshot.find(f => f.id === id);
        
        if (feature && feature.geometry.type === 'Polygon') {
          const coords = feature.geometry.coordinates[0];
          const polygon = coords.map(([lng, lat]) => ({
            latitude: lat,
            longitude: lng
          }));
          
          // Remove last point (duplicate of first for closure)
          if (polygon.length > 1) {
            polygon.pop();
          }
          
          setNewPolygon(polygon);
          setSheetOpen(true);
          
          // Clear the drawn feature
          draw.clear();
        }
      });

      terraDrawRef.current = draw;
      draw.start();
      draw.setMode('polygon');
    } catch (error) {
      console.error('Error initializing TerraDraw:', error);
      toast.error('Error al inicializar herramienta de dibujo');
      setIsDrawing(false);
    }
  }, [editingZone?.color]);

  const resetDrawing = () => {
    if (terraDrawRef.current) {
      try {
        terraDrawRef.current.stop();
        terraDrawRef.current = null;
      } catch (e) {
        console.warn('Error stopping TerraDraw:', e);
      }
    }
    setNewPolygon(null);
    setEditingZone(null);
    setIsDrawing(false);
  };

  const startNewZone = () => {
    if (mapsError || !mapsReady || !mapRef.current) {
      toast.error('Google Maps no está disponible. Configura una API Key válida en Settings > Maps');
      return;
    }
    
    resetDrawing();
    setEditingZone({
      name: '',
      description: '',
      color: ZONE_COLORS[zones.length % ZONE_COLORS.length]
    });
    setIsDrawing(true);
    
    // Initialize TerraDraw after state update
    setTimeout(() => {
      if (mapRef.current) {
        initTerraDraw(mapRef.current);
      }
    }, 100);
  };

  const editZone = (zone) => {
    setEditingZone({ ...zone });
    setNewPolygon(null);
    setSheetOpen(true);
  };

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const toggleZoneVisibility = (zoneId) => {
    setVisibleZones(prev => ({ ...prev, [zoneId]: !prev[zoneId] }));
  };

  if (loadError) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-red-500">
            <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Error al cargar Google Maps. Verifica la configuración de la API Key.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!isLoaded || loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="coverage-section">
      {/* Configuration Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-[#E63946]" />
              <CardTitle>Configuración de Cobertura</CardTitle>
            </div>
            <Button onClick={handleSaveConfig} disabled={saving} size="sm" data-testid="coverage-save-config-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Guardar
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div>
                <Label>Rechazar fuera de cobertura</Label>
                <p className="text-xs text-muted-foreground">Rechazar automáticamente solicitudes fuera del área</p>
              </div>
              <Switch
                checked={config.reject_outside_coverage}
                onCheckedChange={(checked) => setConfig(prev => ({ ...prev, reject_outside_coverage: checked }))}
                data-testid="coverage-reject-switch"
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div>
                <Label>Permitir override manual</Label>
                <p className="text-xs text-muted-foreground">Permitir aprobar manualmente direcciones rechazadas</p>
              </div>
              <Switch
                checked={config.allow_manual_override}
                onCheckedChange={(checked) => setConfig(prev => ({ ...prev, allow_manual_override: checked }))}
                data-testid="coverage-override-switch"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Map Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-[#E63946]" />
              <CardTitle>Zonas de Cobertura</CardTitle>
            </div>
            <div className="flex gap-2">
              {isDrawing ? (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={resetDrawing} data-testid="coverage-cancel-drawing-btn">
                    <X className="h-4 w-4 mr-2" />
                    Cancelar
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={startNewZone} 
                  disabled={mapsError || !mapsReady}
                  data-testid="coverage-new-zone-btn"
                  title={mapsError ? 'Google Maps no está disponible' : ''}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva Zona
                </Button>
              )}
            </div>
          </div>
          <CardDescription>
            {isDrawing 
              ? 'Haz clic en el mapa para dibujar los vértices del polígono. Haz doble clic para cerrar el polígono.'
              : mapsError
                ? 'Error al cargar Google Maps. Verifica tu API Key en Settings > Maps'
                : !mapsApiKey 
                  ? 'Configura Google Maps API Key en Settings > Maps para habilitar el dibujo de zonas'
                  : 'Dibuja polígonos en el mapa para definir las áreas de cobertura'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Test Address */}
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                value={testAddress}
                onChange={(e) => setTestAddress(e.target.value)}
                placeholder="Probar dirección (ej: Calle 85 #15-20, Bogotá)"
                onKeyDown={(e) => e.key === 'Enter' && handleTestAddress()}
                data-testid="coverage-test-address-input"
              />
            </div>
            <Button variant="outline" onClick={handleTestAddress} data-testid="coverage-validate-btn">
              <Search className="h-4 w-4 mr-2" />
              Validar
            </Button>
          </div>

          {testResult && (
            <div 
              className={`p-3 rounded-lg ${testResult.is_covered ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'} border`}
              data-testid="coverage-test-result"
            >
              <div className="flex items-center gap-2">
                <Badge variant={testResult.is_covered ? 'default' : 'destructive'}>
                  {testResult.is_covered ? 'Dentro de cobertura' : 'Fuera de cobertura'}
                </Badge>
                {testResult.coverage_zone && (
                  <span className="text-sm">Zona: {testResult.coverage_zone}</span>
                )}
              </div>
              {testResult.geocoded_address && (
                <p className="text-sm text-muted-foreground mt-1">{testResult.geocoded_address}</p>
              )}
              {testResult.rejection_reason && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{testResult.rejection_reason}</p>
              )}
            </div>
          )}

          {/* Drawing mode indicator */}
          {isDrawing && (
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center gap-2">
              <div className="w-3 h-3 bg-amber-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-amber-800 dark:text-amber-200">
                Modo dibujo activo - Haz clic en el mapa para agregar vértices
              </span>
            </div>
          )}

          {/* Map with Error Boundary */}
          <MapErrorBoundary>
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={config.default_center || defaultCenter}
              zoom={config.default_zoom || 11}
              onLoad={onMapLoad}
              options={{
                mapTypeControl: true,
                streetViewControl: false,
                fullscreenControl: true
              }}
            >
              {/* Render existing zones */}
              {zones.map(zone => (
                visibleZones[zone.id] !== false && zone.polygon && (
                  <Polygon
                    key={zone.id}
                    paths={zone.polygon.map(p => ({ lat: p.latitude, lng: p.longitude }))}
                    options={{
                      fillColor: zone.color || '#E63946',
                      fillOpacity: zone.is_active ? 0.3 : 0.1,
                      strokeColor: zone.color || '#E63946',
                      strokeOpacity: zone.is_active ? 1 : 0.4,
                      strokeWeight: selectedZone?.id === zone.id ? 3 : 2
                    }}
                    onClick={() => setSelectedZone(zone)}
                  />
                )
              ))}

              {/* Render new polygon being created */}
              {newPolygon && newPolygon.length >= 3 && (
                <Polygon
                  paths={newPolygon.map(p => ({ lat: p.latitude, lng: p.longitude }))}
                  options={{
                    fillColor: editingZone?.color || ZONE_COLORS[0],
                    fillOpacity: 0.4,
                    strokeColor: editingZone?.color || ZONE_COLORS[0],
                    strokeWeight: 3
                  }}
                />
              )}

              {/* Test marker */}
              {testMarker && (
                <Marker
                  position={testMarker}
                  icon={{
                    path: window.google?.maps?.SymbolPath?.CIRCLE,
                    scale: 10,
                    fillColor: testResult?.is_covered ? '#22c55e' : '#ef4444',
                    fillOpacity: 1,
                    strokeColor: '#fff',
                    strokeWeight: 2
                  }}
                />
              )}
            </GoogleMap>
          </MapErrorBoundary>

          {/* Zone List */}
          <Separator />
          <div className="space-y-2">
            <h4 className="font-medium flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Zonas configuradas ({zones.length})
            </h4>
            {zones.length === 0 ? (
              <p className="text-sm text-muted-foreground">No hay zonas de cobertura configuradas</p>
            ) : (
              <div className="space-y-2" data-testid="coverage-zones-list">
                {zones.map(zone => (
                  <div
                    key={zone.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      selectedZone?.id === zone.id ? 'border-primary bg-primary/5' : ''
                    }`}
                    onClick={() => setSelectedZone(zone)}
                    data-testid={`coverage-zone-item-${zone.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: zone.color || '#E63946' }}
                      />
                      <div>
                        <span className="font-medium">{zone.name}</span>
                        {zone.description && (
                          <p className="text-xs text-muted-foreground">{zone.description}</p>
                        )}
                      </div>
                      <Badge variant={zone.is_active ? 'default' : 'secondary'}>
                        {zone.is_active ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); toggleZoneVisibility(zone.id); }}
                        title={visibleZones[zone.id] !== false ? 'Ocultar' : 'Mostrar'}
                        data-testid={`coverage-zone-visibility-${zone.id}`}
                      >
                        {visibleZones[zone.id] !== false ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); editZone(zone); }}
                        data-testid={`coverage-zone-edit-${zone.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, zone }); }}
                        data-testid={`coverage-zone-delete-${zone.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Zone Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{editingZone?.id ? 'Editar Zona' : 'Nueva Zona de Cobertura'}</SheetTitle>
            <SheetDescription>
              {editingZone?.id 
                ? 'Modifica los detalles de la zona de cobertura'
                : 'Define el nombre y propiedades de la nueva zona'}
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4 mt-6">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={editingZone?.name || ''}
                onChange={(e) => setEditingZone(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Zona Norte Bogotá"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Input
                value={editingZone?.description || ''}
                onChange={(e) => setEditingZone(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descripción opcional"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex gap-2 flex-wrap">
                {ZONE_COLORS.map(color => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded border-2 ${
                      editingZone?.color === color ? 'border-foreground' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setEditingZone(prev => ({ ...prev, color }))}
                  />
                ))}
              </div>
            </div>

            {editingZone?.id && (
              <div className="flex items-center justify-between">
                <Label>Estado activo</Label>
                <Switch
                  checked={editingZone?.is_active !== false}
                  onCheckedChange={(checked) => setEditingZone(prev => ({ ...prev, is_active: checked }))}
                />
              </div>
            )}

            {!editingZone?.id && !newPolygon && (
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  Primero dibuja el polígono en el mapa haciendo clic en &ldquo;Nueva Zona&rdquo; y luego dibujando los vértices.
                </p>
              </div>
            )}

            {newPolygon && newPolygon.length >= 3 && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600" />
                <p className="text-sm text-green-800 dark:text-green-200">
                  Polígono dibujado con {newPolygon.length} vértices
                </p>
              </div>
            )}

            <Button
              className="w-full"
              onClick={editingZone?.id ? handleUpdateZone : handleCreateZone}
              disabled={saving || !editingZone?.name || (!editingZone?.id && (!newPolygon || newPolygon.length < 3))}
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingZone?.id ? 'Actualizar Zona' : 'Crear Zona'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Zona de Cobertura</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de eliminar la zona &ldquo;{deleteDialog.zone?.name}&rdquo;? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteZone} className="bg-destructive hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CoverageMapSection;
